import { useQuery } from '@tanstack/react-query';
import {
  ENGAGEMENT_ID_OUTPUT_KEY,
  requiresEngagementIdMapping,
} from '@reach/shared-types/tool-engagement';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { appRoutes } from '../../app-routes';
import { credentialDisplayName, partnerApi, type HttpToolDefinitionInput } from '../../lib/api';
import { EngagementActionSection } from './EngagementActionSection';
import { JsonField } from './JsonField';
import { RequestPreview } from './RequestPreview';
import { ResponseMappingSection } from './ResponseMappingSection';
import { TemplateInput } from './TemplateInput';
import { ToolFormField as Field } from './ToolFormField';
import { ToolFormSection as Section } from './ToolFormSection';
import { ToolInputsTable } from './ToolInputsTable';
import { TOOL_EXAMPLES } from './tool-examples';
import {
  DEFAULT_CREDENTIAL_SLOT,
  INPUT_NAME_PATTERN,
  classifyUrlTemplateToken,
  deriveAnnotations,
  isTableRepresentable,
  pathTemplateExpressions,
  requestSchemaToRows,
  rowsToRequestMapping,
  rowsToRequestSchema,
  simplePathPlaceholderNames,
  tableRepresentabilityIssue,
  validateInputRows,
  validateToolName,
  type TemplateTokenKind,
  type ToolAuthConfig,
  type ToolInputRow,
} from './tool-inputs';
import styles from './ToolForm.module.css';

interface ToolFormProps {
  draft: HttpToolDefinitionInput;
  onChange: (next: HttpToolDefinitionInput) => void;
  isEditing?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  onInteraction?: () => void;
  /** Canonical `<partnerId>_<slug>` name, available once the tool is saved. */
  canonicalName?: string;
  sampleResponse?: unknown;
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const REQUEST_MAPPING_LOCATIONS = new Set(['body', 'query', 'path', 'headers']);
const REQUEST_SCHEMA_SHAPE_ERROR = 'request_schema must be a JSON object.';
const REQUEST_MAPPING_SHAPE_ERROR =
  'request_mapping must be a JSON object containing body, query, path, or headers.';

/** How the stored secret is attached to the request. `api_key` collapses into the header option. */
const SEND_AS_OPTIONS = [
  { value: 'custom_header', label: 'In a header' },
  { value: 'bearer', label: 'As a Bearer token' },
  { value: 'basic', label: 'As Basic auth' },
] as const;

const normalizeAuthType = (authType: string): string =>
  authType === 'api_key' ? 'custom_header' : authType;

const isJsonObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const validateRequestSchemaJson = (value: unknown): string | null =>
  isJsonObject(value) ? null : REQUEST_SCHEMA_SHAPE_ERROR;

const validateRequestMappingJson = (value: unknown): string | null => {
  if (!isJsonObject(value)) return REQUEST_MAPPING_SHAPE_ERROR;

  for (const [location, fields] of Object.entries(value)) {
    if (!REQUEST_MAPPING_LOCATIONS.has(location) || !isJsonObject(fields)) {
      return REQUEST_MAPPING_SHAPE_ERROR;
    }
    if (
      location !== 'body' &&
      Object.values(fields).some(fieldValue => typeof fieldValue !== 'string')
    ) {
      return `request_mapping.${location} values must be strings.`;
    }
  }
  return null;
};

const jsonObjectKeys = (value: unknown): Set<string> =>
  value && typeof value === 'object' ? new Set(Object.keys(value)) : new Set<string>();

/**
 * Plain-words guidance for a `{{...}}` path block that isn't a bare input name, keyed to what the
 * developer actually typed: an `args.` prefix gets the exact correction, a Reach-filled reference
 * gets pointed at the Base URL (the one place it works), and anything else is an expression the
 * path can't evaluate.
 */
function pathExpressionWarningFor(expression: string | undefined): string | null {
  if (expression === undefined) return null;
  const argReference = /^args\.([\w-]+)$/.exec(expression);
  if (argReference) {
    return `Drop the args. prefix — a path placeholder is just the input's name: {{${argReference[1]}}}.`;
  }
  const reachFilledReference = /^context\.\w+$/.test(expression);
  if (reachFilledReference) {
    return `{{${expression}}} can't go in the path — values Reach fills in only work in the Base URL. Move it up there; the base URL can include path segments, e.g. https://api.yourcompany.com/v1/customers/{{${expression}}}.`;
  }
  return `The path can't evaluate {{${expression}}} — a {{placeholder}} must exactly match an input's name. For anything more complex, set request_mapping.path in the JSON editor.`;
}

export const ToolForm = ({
  draft,
  onChange,
  isEditing = false,
  onValidityChange,
  onInteraction,
  canonicalName,
  sampleResponse,
}: ToolFormProps) => {
  const { data: credentialsData } = useQuery({
    queryKey: ['ai-credentials'],
    queryFn: () => partnerApi.getCredentials(),
  });
  const credentials = credentialsData?.credentials ?? [];

  const set = <K extends keyof HttpToolDefinitionInput>(key: K, value: HttpToolDefinitionInput[K]) => {
    onChange({ ...draft, [key]: value });
  };

  // The draft is the source of truth, but rows are buffered locally so a transient blank or
  // duplicate name doesn't collapse when serialized into request_schema's object keys.
  const [rows, setRows] = useState<ToolInputRow[]>(() =>
    requestSchemaToRows(draft.request_schema, draft.request_mapping, draft.http_method),
  );
  const [rawMode, setRawMode] = useState(
    () =>
      !isTableRepresentable(
        draft.request_schema,
        draft.request_mapping,
        draft.http_method,
        draft.path_template,
      ),
  );
  // Canonical form of the last schema we emitted; lets us ignore our own writes and reseed only
  // when the schema changes from outside (server-seed when editing, or applying an example).
  const emittedSchemaRef = useRef(JSON.stringify(draft.request_schema));

  useEffect(() => {
    const incoming = JSON.stringify(draft.request_schema);
    if (incoming !== emittedSchemaRef.current) {
      emittedSchemaRef.current = incoming;
      setRows(requestSchemaToRows(draft.request_schema, draft.request_mapping, draft.http_method));
      setRawMode(
        !isTableRepresentable(
          draft.request_schema,
          draft.request_mapping,
          draft.http_method,
          draft.path_template,
        ),
      );
    }
  }, [draft.request_schema, draft.request_mapping, draft.http_method, draft.path_template]);

  const rowIssues = useMemo(
    () => validateInputRows(rows, draft.http_method, draft.path_template),
    [rows, draft.http_method, draft.path_template],
  );
  const nameError = isEditing ? null : validateToolName(draft.display_name);
  const engagementIdIsMissing = requiresEngagementIdMapping(
    draft.schema_mapping_refs,
    draft.response_mapping,
  );

  // Raw-mode JSON validity (escape hatch only).
  const [invalidJsonKeys, setInvalidJsonKeys] = useState<Set<string>>(new Set());
  const reportFieldValidity = useCallback((key: string, isValid: boolean) => {
    setInvalidJsonKeys(prev => {
      const wasInvalid = prev.has(key);
      const validityUnchanged = isValid === !wasInvalid;
      if (validityUnchanged) return prev;
      const next = new Set(prev);
      if (isValid) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const reportResponseJsonValidity = useCallback(
    (isValid: boolean) => reportFieldValidity('response_mapping', isValid),
    [reportFieldValidity],
  );
  const reportResponseRowsValidity = useCallback(
    (isValid: boolean) => reportFieldValidity('response_mapping_rows', isValid),
    [reportFieldValidity],
  );
  const reportEngagementValidity = useCallback(
    (isValid: boolean) => reportFieldValidity('engagement_action', isValid),
    [reportFieldValidity],
  );

  useEffect(() => {
    const tableValid = rawMode || rowIssues.length === 0;
    onValidityChange?.(!nameError && tableValid && invalidJsonKeys.size === 0);
  }, [nameError, rawMode, rowIssues.length, invalidJsonKeys, onValidityChange]);

  const setRequestSchema = (schema: Record<string, unknown>) => {
    emittedSchemaRef.current = JSON.stringify(schema);
    set('request_schema', schema);
  };

  const commitRows = (nextRows: ToolInputRow[]) => {
    setRows(nextRows);
    const schema = rowsToRequestSchema(nextRows, draft.path_template);
    emittedSchemaRef.current = JSON.stringify(schema);
    onChange({
      ...draft,
      request_schema: schema,
      request_mapping: rowsToRequestMapping(nextRows, draft.http_method, draft.path_template),
    });
  };

  const setMethod = (method: string) => {
    onChange({
      ...draft,
      http_method: method,
      request_mapping: rawMode
        ? draft.request_mapping
        : rowsToRequestMapping(rows, method, draft.path_template),
      annotations: deriveAnnotations(method),
    });
  };

  // Path edits re-derive the schema and mapping in table mode: an input whose name matches a
  // `{{placeholder}}` moves out of the body/query and becomes required, live.
  const setPathTemplate = (pathTemplate: string) => {
    if (rawMode) {
      set('path_template', pathTemplate);
      return;
    }
    const schema = rowsToRequestSchema(rows, pathTemplate);
    emittedSchemaRef.current = JSON.stringify(schema);
    onChange({
      ...draft,
      path_template: pathTemplate,
      request_schema: schema,
      request_mapping: rowsToRequestMapping(rows, draft.http_method, pathTemplate),
    });
  };

  const authConfig = (draft.auth_config ?? {}) as ToolAuthConfig;
  const credentialSlot = authConfig.credentialSlot || DEFAULT_CREDENTIAL_SLOT;
  const selectedCredentialId = draft.credentials?.[credentialSlot] ?? '';
  const setCredential = (credentialId: string) =>
    onChange({
      ...draft,
      auth_config: { ...authConfig, credentialSlot },
      credentials: { ...draft.credentials, [credentialSlot]: credentialId },
    });
  const setSendAs = (authType: string) => set('auth_type', authType);
  const setHeaderName = (headerName: string) =>
    set('auth_config', { ...authConfig, credentialSlot, headerName });
  const showHeaderName = normalizeAuthType(draft.auth_type) === 'custom_header';

  const representabilityIssue = tableRepresentabilityIssue(
    draft.request_schema,
    draft.request_mapping,
    draft.http_method,
    draft.path_template,
  );
  const canSwitchToSimple = representabilityIssue === null;

  const switchToSimple = () => {
    if (!rawMode) return;
    setRows(requestSchemaToRows(draft.request_schema, draft.request_mapping, draft.http_method));
    reportFieldValidity('request_schema', true);
    reportFieldValidity('request_mapping', true);
    setRawMode(false);
  };

  const isPristine = !isEditing && !draft.display_name && !draft.base_url && rows.length === 0;

  // Path guidance adapts to what's typed: name the inputs that fill placeholders, call out
  // placeholders nothing fills, and point expressions at the JSON editor.
  const pathPlaceholderNames = [...simplePathPlaceholderNames(draft.path_template)];
  const schemaPropertyNames = jsonObjectKeys(draft.request_schema.properties);
  const explicitPathNames = jsonObjectKeys(draft.request_mapping.path);
  const placeholderIsFilled = (name: string) =>
    schemaPropertyNames.has(name) || explicitPathNames.has(name);
  const filledPlaceholderNames = pathPlaceholderNames.filter(placeholderIsFilled);
  const unmatchedPlaceholderNames = pathPlaceholderNames.filter(name => !placeholderIsFilled(name));
  const pathHint =
    filledPlaceholderNames.length > 0
      ? `Appended to the base URL. Filled by your inputs: ${filledPlaceholderNames.join(', ')}.`
      : 'Appended to the base URL. A {{placeholder}} is filled by the input with the same name from step 4 — e.g. /customers/{{customerId}}.';
  const unmatchedTokens = unmatchedPlaceholderNames.map(name => `{{${name}}}`).join(', ');
  const unmatchedPathWarning =
    unmatchedPlaceholderNames.length === 0
      ? null
      : unmatchedPlaceholderNames.length === 1
        ? `${unmatchedTokens} has nothing to fill it — add an input named ${unmatchedPlaceholderNames[0]} in step 4.`
        : `${unmatchedTokens} have nothing to fill them — add inputs with those exact names in step 4.`;
  const pathExpressionWarning = pathExpressionWarningFor(
    pathTemplateExpressions(draft.path_template)[0],
  );
  const pathWarning = pathExpressionWarning ?? unmatchedPathWarning;

  const classifyBaseUrlToken = (token: string) =>
    classifyUrlTemplateToken(token, {
      variables: draft.variables,
      credentials: draft.credentials,
      requestSchema: draft.request_schema,
    });
  const classifyPathToken = (token: string): TemplateTokenKind => {
    const placeholderName = token.slice(2, -2);
    if (!INPUT_NAME_PATTERN.test(placeholderName)) return 'invalid';
    return placeholderIsFilled(placeholderName) ? 'valid' : 'invalid';
  };

  return (
    <div className={styles.form}>
      {isPristine && (
        <aside className={styles.examples}>
          <p className={styles.examplesLead}>New to this? Start from an example and edit it:</p>
          <div className={styles.examplesGrid}>
            {TOOL_EXAMPLES.map(example => (
              <button
                key={example.key}
                type="button"
                onClick={() => onChange(example.draft)}
                className={styles.exampleButton}>
                <span className={styles.exampleName}>{example.label}</span>
                <span className={styles.exampleBlurb}>{example.blurb}</span>
              </button>
            ))}
          </div>
        </aside>
      )}

      <Section
        step={1}
        title="Name and describe the tool"
        subtitle="The agent reads the description to decide when to call this tool.">
        {isEditing && canonicalName ? (
          <Field label="Tool name" hint="The runtime name agents call. Locked after creation.">
            <input type="text" value={canonicalName} readOnly className={styles.monoInput} />
          </Field>
        ) : (
          <Field
            label="Name"
            hint="Lowercase snake_case, starting with a letter (e.g. upsert_customer).">
            <input
              type="text"
              value={draft.display_name}
              onChange={e => set('display_name', e.target.value)}
              disabled={isEditing}
              className={styles.monoInput}
              placeholder="upsert_customer"
              aria-invalid={Boolean(nameError)}
            />
            {nameError && <span className={styles.fieldError}>{nameError}</span>}
          </Field>
        )}
        <Field label="Description" hint="Tell the agent what this does and when to use it.">
          <textarea
            value={draft.description}
            onChange={e => set('description', e.target.value)}
            className={styles.textarea}
            rows={8}
            placeholder="Create or update a customer profile in our system. Use this when an agent needs to persist customer identity or profile details gathered in conversation. This is an upsert operation and may be called multiple times in one conversation as more details become available. Call it whenever the required identifier inputs in the schema are available and there is a new usable customer detail to save. Omit unknown optional fields and never send placeholders. A successful response means our system accepted the supplied fields; it does not necessarily mean the profile is complete."
          />
        </Field>
      </Section>

      <Section
        step={2}
        title="Point it at your API"
        subtitle="When the agent runs this tool, we send an HTTP request to this URL.">
        <Field label="Method">
          <select
            value={draft.http_method}
            onChange={e => setMethod(e.target.value)}
            className={styles.monoSelect}>
            {HTTP_METHODS.map(method => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Base URL"
          hint="The https:// address we call. To include the tenant's ID from your own system, write {{context.tenantExternalId}} where it goes — Reach fills it in on every request.">
          <TemplateInput
            value={draft.base_url}
            onChange={value => set('base_url', value)}
            classifyToken={classifyBaseUrlToken}
            placeholder="https://api.yourcompany.com/v1"
          />
        </Field>
        <Field label="Path" hint={pathHint}>
          <TemplateInput
            value={draft.path_template}
            onChange={setPathTemplate}
            classifyToken={classifyPathToken}
            placeholder="/customers"
          />
          {pathWarning && <span className={styles.fieldError}>{pathWarning}</span>}
        </Field>
      </Section>

      <Section
        step={3}
        title="Authenticate the request"
        subtitle="Pick the credential we send with the request, and how to attach it.">
        <Field label="Credential">
          {credentials.length === 0 ? (
            <p className={styles.helpText}>
              No credentials yet.{' '}
              <Link to={appRoutes.AI_CREDENTIALS} className={styles.subtitleLink}>
                Add a credential first
              </Link>
              , then come back.
            </p>
          ) : (
            <select
              value={selectedCredentialId}
              onChange={e => setCredential(e.target.value)}
              className={styles.select}>
              <option value="">Pick a credential…</option>
              {credentials.map(credential => (
                <option key={credential.id} value={credential.id}>
                  {credentialDisplayName(credential)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Send it">
          <select
            value={normalizeAuthType(draft.auth_type)}
            onChange={e => setSendAs(e.target.value)}
            className={styles.select}>
            {SEND_AS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        {showHeaderName && (
          <Field label="Header name">
            <input
              type="text"
              value={authConfig.headerName ?? ''}
              onChange={e => setHeaderName(e.target.value)}
              className={styles.monoInput}
              placeholder="x-api-key"
            />
          </Field>
        )}
      </Section>

      <Section
        step={4}
        title="Define the tool's inputs"
        subtitle="Each row is one input — a value we send to your API every time the agent runs this tool.">
        <div className={styles.editorModeRow}>
          <button
            type="button"
            aria-pressed={!rawMode}
            disabled={rawMode && !canSwitchToSimple}
            onClick={switchToSimple}>
            Simple
          </button>
          <button type="button" aria-pressed={rawMode} onClick={() => setRawMode(true)}>
            JSON
          </button>
          {rawMode && representabilityIssue && (
            <span className={styles.editorModeReason}>{representabilityIssue}</span>
          )}
        </div>
        {rawMode ? (
          <>
            <JsonField
              label="request_schema"
              hint="JSON Schema describing the inputs the agent provides."
              value={draft.request_schema}
              onChange={value => setRequestSchema(value as Record<string, unknown>)}
              validateValue={validateRequestSchemaJson}
              onValidityChange={value => reportFieldValidity('request_schema', value)}
              onEdit={onInteraction}
              height="200px"
            />
            <JsonField
              label="request_mapping"
              hint="Maps inputs into the request. Group fields under body, query, headers, or path."
              value={draft.request_mapping}
              onChange={value => set('request_mapping', value as Record<string, unknown>)}
              validateValue={validateRequestMappingJson}
              onValidityChange={value => reportFieldValidity('request_mapping', value)}
              onEdit={onInteraction}
              height="200px"
            />
          </>
        ) : (
          <ToolInputsTable
            rows={rows}
            issues={rowIssues}
            method={draft.http_method}
            pathTemplate={draft.path_template}
            onChange={commitRows}
          />
        )}
      </Section>

      <Section title="Request preview" subtitle="Exactly what we send when the agent runs the tool.">
        <RequestPreview draft={draft} />
      </Section>

      <EngagementActionSection
        step={5}
        schemaMappingRefs={draft.schema_mapping_refs}
        responseMapping={draft.response_mapping}
        onChange={value => set('schema_mapping_refs', value)}
        onValidityChange={reportEngagementValidity}
      />

      <ResponseMappingSection
        step={6}
        mapping={draft.response_mapping}
        sampleResponse={sampleResponse}
        requiredOutputName={engagementIdIsMissing ? ENGAGEMENT_ID_OUTPUT_KEY : undefined}
        onChange={value => set('response_mapping', value)}
        onJsonValidityChange={reportResponseJsonValidity}
        onRowsValidityChange={reportResponseRowsValidity}
        onInteraction={onInteraction}
      />

      {isEditing && (
        <Section
          title="Status"
          subtitle="Active tools are available to agents; inactive tools are hidden from them.">
          <Field label="Status">
            <select
              value={draft.status}
              onChange={e => set('status', e.target.value as 'active' | 'inactive')}
              className={styles.select}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
        </Section>
      )}
    </div>
  );
};
